import { Company } from './company';
import { api, writeDB } from './store';
import { User } from './user';
import { State } from './state';
import { DB } from 'redux-database';
import { useData } from './data';
import { apollo } from './graphql';
import { REGENERATE_API_TOKEN } from './queries/membership';
import { t } from '../locales/translate';
import { history } from './history';
import { dashboardPath } from '../routes';
import { MembershipRole } from '../generated/graphql';

/* INTERFACES */
export type MembershipPermission = 'admin' | 'merch_admin' | 'ticketing_admin' | 'financial';

export interface Membership {
  id: string;
  role: MembershipRole;
  user?: User;
  comment: string;
  token?: string | null;
  company: Company;
  permissions: MembershipPermission[];
  $permissions: Array<'create' | 'update' | 'destroy' | 'regenerate_token' | 'grant_financial'>;
  indexPermissions: string[];
  invitedEmail?: string;
  createdAt?: string;
  expiresAt?: string;
  invitedBy: {
    email: string;
    name?: string;
  };
  companyId?: string;
  calendarFeedUrl?: string;
}

export interface RequestMembership {
  id?: string;
  role?: MembershipRole;
  email?: string;
  userId?: string;
  comment?: string;
  companyId: string;
  permissions?: MembershipPermission[];
  createdAt?: string;
  expiresAt?: string;
  invitedEmail?: string;
  $permissions?: Array<'create' | 'update' | 'destroy' | 'regenerate_token' | 'grant_financial'>;
}

/* MEMBERSHIPS CREATE */
export async function createMembership(model: RequestMembership): Promise<void> {
  const response = (await api.createMembership(model)).data;
  writeDB.table('memberships').insert(response);
}

/* MEMBERSHIPS READ */

/* MEMBERSHIPS UPDATE */
export async function updateMembership(membershipId: string, model: RequestMembership): Promise<void> {
  const response = await api.updateMembership({ membershipId, ...model });

  // TODO: Update api model to match local interface
  const data = response.data as unknown as Membership;
  writeDB.table('memberships').update(membershipId, data);
}

/* MEMBERSHIPS DELETE */
export async function deleteMembership(membershipId: string): Promise<void> {
  await api.destroyMembership({ membershipId });
  writeDB.table('memberships').delete(membershipId);
}

/* HELPERS */
export function isAdmin(membership?: Membership): boolean {
  return !!membership && membership.permissions.includes('admin');
}

export function ticketingActivated(membership?: Membership): boolean {
  return (
    !!membership &&
    (isAdmin(membership) ||
      membership.permissions.includes('ticketing_admin') ||
      membership.company.features.includes('ticketing'))
  );
}

export function merchActivated(membership?: Membership): boolean {
  return (
    !!membership &&
    (isAdmin(membership) ||
      membership.permissions.includes('merch_admin') ||
      membership.company.features.includes('merch'))
  );
}

export function ordersActivated(membership?: Membership): boolean {
  return isAdmin(membership) && (ticketingActivated(membership) || merchActivated(membership));
}

export function selectMembership(membershipId: string, reload = true): void {
  writeDB.set('selectedMembership', membershipId);
  history.push(dashboardPath());
  if (reload) {
    location.reload();
  }
}

export function reloadOnMembershipChange(settingsKey: string): void {
  window.addEventListener('storage', (e) => {
    if (e.key !== settingsKey) return;

    const oldMembership = JSON.parse(e.oldValue || '{}')['selectedMembership'];
    const newMembership = JSON.parse(e.newValue || '{}')['selectedMembership'];
    if (oldMembership === newMembership) return;

    const newLocation = new URL(location.toString());
    newLocation.searchParams.set('reloadNotification', 'companyChanged');
    location.assign(newLocation);
  });
}

export function showReloadNotificationCompanyChanged(): void {
  writeDB.table('notifications').insert({ ...t.model.membership.membershipChanged, type: 'success' });
}

export function selectedMembership(db: DB<State>): Membership | undefined {
  const id = db.get('selectedMembership');
  if (!id) return;
  return db.table('memberships').find(id);
}

export function useSessionMemberships(): Membership[] {
  return useData((db) => {
    const session = db.get('session');
    return db.table('memberships').where((e) => !!session?.membershipIds?.includes(e.id));
  });
}

export function useSelectedMembership(): Membership | undefined {
  return useData(selectedMembership);
}

export function useIsAdmin(): boolean {
  return isAdmin(useSelectedMembership());
}

function getSelectedMembership(): Membership | undefined {
  const id = writeDB.get('selectedMembership');
  if (!id) {
    return;
  }
  return writeDB.table('memberships').find(id);
}

export async function regenerateAPIToken(): Promise<void> {
  const membership = getSelectedMembership();
  if (!membership) {
    return;
  }
  const response = await apollo.mutate({
    mutation: REGENERATE_API_TOKEN,
    variables: {},
  });
  const token = response.data?.regenerateApiToken?.membership.token;
  if (token) {
    writeDB.table('memberships').update(membership, { token });
  }
}
